import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { api, hasAccessToken, signOut as endSession } from '../api';

type User = components['schemas']['User'];
type BookingList = components['schemas']['BookingList'];
type NotificationList = components['schemas']['NotificationList'];

// Who is signed in on the website, for the header account menu, the nav drawer and the
// tab-bar badges (design DC:162-176, DC:4735-4744).
//
// The website only asks the API about the visitor when it already holds an access
// token (set by the OTP sign-in or the booking stepper, AP-D4: in memory, never in
// storage). A signed-out visitor therefore costs no /me request and no 401 round trip.
// The check re-runs on navigation because the token is set by a screen, not here.
//
// Badges mirror the design's two counters: `attention` = bookings waiting on the
// customer (payment_verification or rejected proof — the Bookings dot and the "My
// bookings" badge), `unread` = unread notifications (the More count and the
// "Notifications" badge). Both are best-effort: a failed count is 0, never an error.

export interface SignedInAccount {
  user: User | null;
  attention: number;
  unread: number;
  signOut: () => Promise<void>;
}

const AccountContext = createContext<SignedInAccount | null>(null);

async function countItems(load: () => Promise<{ items?: unknown[] }>): Promise<number> {
  try {
    return (await load()).items?.length ?? 0;
  } catch {
    return 0;
  }
}

export function SignedInAccountProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [attention, setAttention] = useState(0);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!hasAccessToken()) {
      setUser(null);
      return;
    }
    if (user) return;
    let alive = true;
    (async () => {
      try {
        const me = (await api.GET('/me')) as User;
        if (!alive) return;
        setUser(me);
        const [pv, rj, notes] = await Promise.all([
          countItems(() => api.GET('/me/bookings', { params: { query: { status: 'payment_verification' } } }) as Promise<BookingList>),
          countItems(() => api.GET('/me/bookings', { params: { query: { status: 'rejected' } } }) as Promise<BookingList>),
          (async () => {
            try {
              const list = (await api.GET('/me/notifications')) as NotificationList;
              return (list.items ?? []).filter((n) => !n.read_at).length;
            } catch {
              return 0;
            }
          })(),
        ]);
        if (!alive) return;
        setAttention(pv + rj);
        setUnread(notes);
      } catch {
        if (alive) setUser(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [pathname, user]);

  const signOut = useCallback(async () => {
    await endSession();
    setUser(null);
    setAttention(0);
    setUnread(0);
  }, []);

  const value = useMemo(() => ({ user, attention, unread, signOut }), [user, attention, unread, signOut]);
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

// Outside the provider (isolated component tests) the visitor reads as signed out.
const SIGNED_OUT: SignedInAccount = { user: null, attention: 0, unread: 0, signOut: async () => {} };

export function useSignedInAccount(): SignedInAccount {
  return useContext(AccountContext) ?? SIGNED_OUT;
}
